import { useChat } from '@/composables/useChat';
import { defineStore } from 'pinia';
import {
  ApiIncident,
  ChatState,
  FileMessage,
  LoadMessagesData,
  Message,
  MessagesData,
} from '@/interfaces/chat/chat.interface';
import { BASE_URL } from '@/utils/constants/api-constants';
import { getData, postData } from '@/services/axios-service';

const INITIAL_STATE = {
  chatMessages: [],
  chatId: '',
  page: 1,
  socket: null,
  isSomeoneInChat: false,
};

export const useChatStore = defineStore({
  id: 'chat',
  state: (): ChatState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    async getIncidents(bookingId: string) {
      try {
        const { data, success, error } = await getData<ApiIncident[]>(
          `https://${BASE_URL.CHAT}api/tenant/incidents/by-reservation/${bookingId}`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async uploadFile(file: FileMessage, bookingId: string) {
      try {
        const { data, success, error } = await postData<FileMessage>(
          `https://${BASE_URL.CHAT}api/media/uploadFile`,
          {
            ...file,
            reservationId: bookingId,
          },
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    async createIncident(payload: {
      reservationId: string;
      title: string;
      description: string;
      severity: 'High' | 'Medium';
      imgArray?: { base64File: string; fileName: string }[];
    }) {
      try {
        const { data, success, error } = await postData(
          `https://${BASE_URL.CHAT}api/tenant/incidents`,
          payload,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },

    resetStore() {
      this.chatMessages = [];
      this.chatId = '';
      this.page = 1;
    },
    setMessages(messages: MessagesData) {
      const { formatMessagesByDate, scrollToBottom } = useChat();
      this.chatMessages = formatMessagesByDate(messages.rows);
      scrollToBottom();
    },

    setNewMessage(message: Message) {
      const { scrollToBottom } = useChat();

      const localDate = new Date(message.sentAt as string);
      const formattedDate = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;

      this.chatMessages.find(([date]) => date === formattedDate);

      const dateExisting = this.chatMessages.find(([date]) => date === formattedDate);

      if (dateExisting) {
        dateExisting[1].push(message);
      } else {
        this.chatMessages.push([formattedDate, [message]]);
      }

      scrollToBottom();
    },

    setLoadMoreMessages(args: LoadMessagesData) {
      const { scrollToHeight } = useChat();

      const messages = args.data.rows;

      messages.forEach((newMessage) => {
        const localDate = new Date(newMessage.createdAt as string);
        const formattedDate = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;

        const fechaExistente = this.chatMessages.find(([date]) => date === formattedDate);

        if (fechaExistente) {
          // Si ya existe, agregar el nuevo mensaje
          fechaExistente[1].push(newMessage);
        } else {
          // Si no existe, crear un nuevo array para esa fecha
          this.chatMessages.push([formattedDate, [newMessage]]);
        }

        // Ordenar los mensajes para esa fecha después de agregar el nuevo mensaje
        const messagesDates = this.chatMessages.find(([date]) => date === formattedDate);
        if (messagesDates) {
          messagesDates[1].sort(
            (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        }
      });

      // Ordenar las fechas en this.chatMessages después de agregar todos los nuevos mensajes
      this.chatMessages.sort(
        ([dateA], [dateB]) => new Date(dateA).getTime() - new Date(dateB).getTime(),
      );

      this.page = args.page;

      scrollToHeight();
    },
  },
});

export type AuthStore = ReturnType<typeof useChatStore>;
